import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import Typography from 'apollo-react/components/Typography';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';
import StatusCheck from 'apollo-react-icons/StatusCheck';
import { useMultiForm } from '../../../../../contexts/MultiFormContext';
import {
  FormikTextField,
  FormikDocumentUpload,
  FormikDatePicker,
} from '../../../../common/FormikComponent';
import axiosInstance from '../../../../../axiosInstance';
import CommentsList from '../../../../common/FormApproveComponents/CommentsList/CommentsList';
import CommentToggle from '../../../../common/CommentToggle/CommentToggle';
import PhoneNumberInput from 'apollo-react/components/PhoneNumberInput';

const FormikContextSync = ({ onValuesChange }) => {
  const { values } = useFormikContext();
  const prevValuesRef = useRef(null);
  useEffect(() => {
    const valuesStr = JSON.stringify(values);
    if (prevValuesRef.current !== valuesStr) {
      prevValuesRef.current = valuesStr;
      onValuesChange(values);
    }
  }, [values, onValuesChange]);
  return null;
};

const EMIRATES_ID_REGEX = /^\d{15}$/;
const LICENSE_FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png';

/** Apollo DatePicker expects a Moment (or compatible) with .format(); drafts/API restore ISO strings. */
const toDatePickerValue = (val) => {
  if (val == null || val === '') {
    return null;
  }
  if (moment.isMoment(val)) {
    return val;
  }
  if (val instanceof Date) {
    const m = moment(val);
    return m.isValid() ? m : null;
  }
  const m = moment(val);
  return m.isValid() ? m : null;
};

const renewalDayMs = (v) => {
  if (v == null || v === '') {
    return null;
  }
  const m = moment.isMoment(v)
    ? v.clone().startOf('day')
    : moment(v).startOf('day');
  return m.isValid() ? m.valueOf() : null;
};

/** When baselines exist (renewal), new issue/end must be strictly after previous issue and end dates. */
const buildHcpYupSchema = (t, baselineIssue, baselineEnd) => {
  const bi = renewalDayMs(baselineIssue);
  const be = renewalDayMs(baselineEnd);
  const hasRenewalBaseline = bi != null && be != null;

  let licenseIssueDate = Yup.date()
    .required('License issue date is required')
    .typeError('Invalid date');
  let licenseEndDate = Yup.date()
    .required('License end date is required')
    .typeError('Invalid date')
    .min(Yup.ref('license_issue_date'), t('HcpLicenseEndAfterIssue'));

  if (hasRenewalBaseline) {
    licenseIssueDate = licenseIssueDate.test(
      'renew-issue-baseline',
      function (value) {
        const v = renewalDayMs(value);
        if (v == null) {
          return true;
        }
        if (v <= be || v <= bi) {
          return this.createError({ message: t('renewalNewIssueDateRule') });
        }
        return true;
      }
    );
    licenseEndDate = licenseEndDate.test(
      'renew-end-baseline',
      function (value) {
        const v = renewalDayMs(value);
        if (v == null) {
          return true;
        }
        if (v <= be || v <= bi) {
          return this.createError({ message: t('renewalNewEndDateRule') });
        }
        return true;
      }
    );
  }

  return Yup.object({
    hcp_name: Yup.string()
      .trim()
      .max(150, 'Max 150 characters')
      .required('HCP Name is required'),
    hcp_emirates_id: Yup.string()
      .transform((v) => (v == null ? '' : String(v).replace(/\D/g, '')))
      .required('HCP Emirates ID is required')
      .matches(EMIRATES_ID_REGEX, 'Enter a valid 15-digit Emirates ID'),
    hcp_email: Yup.string()
      .trim()
      .email('Valid email required')
      .required('HCP Email is required'),
    hcp_phone: Yup.string().required('HCP Phone is required'),
    license_number: Yup.string().trim().required('License # is required'),
    license_issue_date: licenseIssueDate,
    license_end_date: licenseEndDate,
    license_image_file: Yup.mixed().test(
      'license',
      'Attach license image (PDF, JPG, PNG)',
      (val) =>
        Array.isArray(val) &&
        val.length > 0 &&
        (val[0]?.id || val[0]?.original_name)
    ),
    total_cme_hours: Yup.number()
      .transform((v, o) => (o === '' || o == null ? undefined : v))
      .min(0, 'Must be ≥ 0')
      .nullable(),
    additional_attachments: Yup.mixed().nullable(),
  });
};

const HcpLicensesList = ({ isSendbackMode }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const requestNameParam = searchParams.get('request_name');
  const emiratesIdFromUrl = useMemo(() => {
    if (requestNameParam == null || String(requestNameParam).trim() === '') {
      return '';
    }
    return String(requestNameParam).replace(/\D/g, '');
  }, [requestNameParam]);
  const lockEmiratesIdFromUrl = useMemo(
    () => requestNameParam != null && String(requestNameParam).trim() !== '',
    [requestNameParam]
  );

  const formRef = useRef(null);
  const {
    hcpLicensesList,
    setFormDataWithValidation,
    errors,
    validity,
    hcpLicenseRenewalLockIdentity,
  } = useMultiForm();
  const lockIdentityFields = Boolean(hcpLicenseRenewalLockIdentity);
  const disableEmiratesIdField = lockIdentityFields || lockEmiratesIdFromUrl;

  const validationSchema = useMemo(
    () =>
      buildHcpYupSchema(
        t,
        hcpLicensesList?.renewalBaselineIssueDate,
        hcpLicensesList?.renewalBaselineEndDate
      ),
    [
      t,
      hcpLicensesList?.renewalBaselineIssueDate,
      hcpLicensesList?.renewalBaselineEndDate,
    ]
  );

  const [comments, setComments] = useState([]);
  const hcpLicensesListId = useMemo(
    () => hcpLicensesList?.id,
    [hcpLicensesList?.id]
  );

  const fetchComments = useCallback(async () => {
    if (!hcpLicensesListId) return;
    try {
      const response = await axiosInstance.get(
        `/api/hcp-licenses-list-comments/enduser/${hcpLicensesListId}`
      );
      setComments(response.data || []);
    } catch (error) {
      console.error('error fetching comments:', error);
    }
  }, [hcpLicensesListId]);

  useEffect(() => {
    if (hcpLicensesListId) {
      fetchComments();
    }
  }, [hcpLicensesListId, fetchComments]);

  const handleAddComment = async (
    comment,
    _subFormId,
    fileId = null,
    fileData = null
  ) => {
    try {
      const requestData = { hcpLicensesListId: hcpLicensesList?.id, comment };
      if (fileId) {
        requestData.additional_file_id = fileId;
      }
      const response = await axiosInstance.post(
        `/api/hcp-licenses-list-comments/enduser`,
        requestData
      );
      const enrichedComment = { ...response.data, isOwner: true };
      if (fileData && fileId) {
        enrichedComment.additional_file = {
          id: fileData.id,
          original_name: fileData.original_name,
          file_path: null,
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          createdAt: fileData.created_at,
          updatedAt: fileData.created_at,
          deletedAt: null,
          user_id: null,
          end_user_id: null,
          ...fileData,
        };
      }
      setComments((prev) => [enrichedComment, ...prev]);
    } catch (error) {
      console.error('error adding comment:', error);
      throw error;
    }
  };

  const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
    try {
      const requestData = { comment: newText };
      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }
      await axiosInstance.put(
        `/api/hcp-licenses-list-comments/enduser/${commentId}`,
        requestData
      );
      fetchComments();
    } catch (error) {
      console.error('error editing comment:', error);
      throw error;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axiosInstance.delete(
        `/api/hcp-licenses-list-comments/enduser/${commentId}`
      );
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (error) {
      console.error('error deleting comment:', error);
      throw error;
    }
  };

  const syncTimeoutRef = useRef(null);
  const lastSyncedValuesRef = useRef(null);
  /** Latest Formik values; the License tab unmounts when switching subforms, so debounced sync alone can drop edits. */
  const latestValuesRef = useRef(null);
  const handleValuesSync = useCallback(
    (values) => {
      latestValuesRef.current = values;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      const currentValuesString = JSON.stringify(values);
      if (lastSyncedValuesRef.current === currentValuesString) {
        return;
      }
      syncTimeoutRef.current = setTimeout(() => {
        const snapshot = latestValuesRef.current;
        if (!snapshot) {
          return;
        }
        const latest = JSON.stringify(snapshot);
        if (lastSyncedValuesRef.current !== latest) {
          lastSyncedValuesRef.current = latest;
          setFormDataWithValidation('hcpLicensesList', snapshot);
        }
      }, 150);
    },
    [setFormDataWithValidation]
  );

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      const pending = latestValuesRef.current;
      if (!pending) {
        return;
      }
      const pendingStr = JSON.stringify(pending);
      if (lastSyncedValuesRef.current !== pendingStr) {
        lastSyncedValuesRef.current = pendingStr;
        setFormDataWithValidation('hcpLicensesList', pending);
      }
    };
  }, [setFormDataWithValidation]);

  const initialValues = useMemo(
    () => ({
      hcp_name: hcpLicensesList.hcp_name || '',
      hcp_emirates_id: lockEmiratesIdFromUrl
        ? emiratesIdFromUrl
        : hcpLicensesList.hcp_emirates_id || '',
      hcp_email: hcpLicensesList.hcp_email || '',
      hcp_phone: hcpLicensesList.hcp_phone || '',
      license_number: hcpLicensesList.license_number || '',
      license_issue_date: toDatePickerValue(hcpLicensesList.license_issue_date),
      license_end_date: toDatePickerValue(hcpLicensesList.license_end_date),
      license_image_file: hcpLicensesList.license_image_file || [],
      total_cme_hours:
        hcpLicensesList.total_cme_hours === null ||
        hcpLicensesList.total_cme_hours === undefined
          ? ''
          : String(hcpLicensesList.total_cme_hours),
      additional_attachments: hcpLicensesList.additional_attachments || [],
    }),
    [hcpLicensesList, lockEmiratesIdFromUrl, emiratesIdFromUrl]
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnChange
        validateOnBlur
      >
        {({ values, errors: formikErrors }) => {
          const contextErrors = errors.hcpLicensesList;
          const hasContextErrors = contextErrors?.hasErrors;
          const generalError = contextErrors?.general;
          const combinedFieldErrors = { ...formikErrors };
          if (contextErrors?.fields) {
            Object.entries(contextErrors.fields).forEach(
              ([fieldName, fieldError]) => {
                if (!combinedFieldErrors[fieldName] && fieldError?.error) {
                  combinedFieldErrors[fieldName] = fieldError.error;
                }
              }
            );
          }
          const displayErrors =
            hasContextErrors && generalError
              ? { general: generalError }
              : combinedFieldErrors;

          return (
            <Form ref={formRef}>
              <FormikContextSync onValuesChange={handleValuesSync} />

              <Box sx={{ marginBottom: 4 }}>
                <Typography
                  variant="title1"
                  component="h2"
                  sx={{
                    fontWeight: 500,
                    marginBottom: 3,
                    color: '#333',
                    paddingBottom: 1,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  License Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormikTextField
                      name="hcp_name"
                      label="HCP Name"
                      required
                      fullWidth
                      disabled={lockIdentityFields}
                      inputProps={{ maxLength: 150 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormikTextField
                      name="hcp_emirates_id"
                      label="HCP Emirates ID #"
                      required
                      fullWidth
                      disabled={disableEmiratesIdField}
                      placeholder="15 digits"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormikTextField
                      name="hcp_email"
                      label="HCP Email"
                      type="email"
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PhoneBlock
                      value={values.hcp_phone}
                      error={!!formikErrors.hcp_phone}
                      helperText={formikErrors.hcp_phone}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormikTextField
                      name="license_number"
                      label="License #"
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormikDatePicker
                      name="license_issue_date"
                      label="License Issue Date"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormikDatePicker
                      name="license_end_date"
                      label="License Renewal/End Date"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormikTextField
                      name="total_cme_hours"
                      label="Total CME Hours (optional)"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ marginBottom: 4 }}>
                <Typography
                  variant="title1"
                  component="h2"
                  sx={{
                    fontWeight: 500,
                    marginBottom: 2,
                    color: '#333',
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: 1,
                  }}
                >
                  Documents
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormikDocumentUpload
                      name="license_image_file"
                      label="Attach License Image"
                      helperText="Required: PDF, JPG, or PNG"
                      accept={LICENSE_FILE_ACCEPT}
                      multiple={false}
                      maxFiles={1}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormikDocumentUpload
                      name="additional_attachments"
                      label="Additional Attachments (optional)"
                      helperText="Multiple files allowed"
                      multiple
                      maxFiles={10}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: validity.hcpLicensesList
                    ? '#e8f5e8'
                    : '#f5f5f5',
                  borderRadius: 1,
                  border: validity.hcpLicensesList
                    ? '1px solid #4caf50'
                    : '1px solid #ddd',
                  marginBottom: 5,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {validity.hcpLicensesList && (
                    <StatusCheck size={20} color="#4caf50" />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: validity.hcpLicensesList ? '#4caf50' : '#666',
                      fontWeight: validity.hcpLicensesList ? 'bold' : 'normal',
                    }}
                  >
                    {validity.hcpLicensesList
                      ? 'License Information is valid and ready for submission'
                      : 'Complete all required fields to enable submission'}
                  </Typography>
                </div>
              </Box>

              {displayErrors && Object.keys(displayErrors).length > 0 && (
                <Box
                  sx={{
                    marginTop: 2,
                    padding: 2,
                    backgroundColor: '#ffebee',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="error" fontWeight="bold">
                    {t('Please fix the following errors')}
                  </Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    {displayErrors.general != null &&
                    String(displayErrors.general).trim() !== ''
                      ? String(displayErrors.general)
                          .split(/\n\n+/)
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .map((line, idx) => (
                            <li key={`general-${idx}`}>
                              <Typography variant="caption" color="error">
                                {line}
                              </Typography>
                            </li>
                          ))
                      : Object.entries(displayErrors).map(([field, error]) => (
                          <li key={field}>
                            <Typography variant="caption" color="error">
                              {typeof error === 'string'
                                ? error
                                : JSON.stringify(error)}
                            </Typography>
                          </li>
                        ))}
                  </ul>
                </Box>
              )}
            </Form>
          );
        }}
      </Formik>

      <div
        className="form-comment-section"
        style={{
          isolation: 'isolate',
          position: 'relative',
          zIndex: 1,
          marginTop: '35px',
        }}
      >
        <CommentsList
          prevComments={comments}
          pagination={{}}
          filters={{}}
          updateFilters={() => {}}
          showControls={false}
          showFilters={false}
          showSorting={false}
          showPageSize={false}
          showResultsInfo={false}
          showPagination={false}
          persona="enduser"
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
        />
        {isSendbackMode && (
          <CommentToggle
            onAddComment={handleAddComment}
            persona="enduser"
            buttonText="Comment"
            buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
            hasComments={comments && comments.length > 0}
          />
        )}
      </div>
    </>
  );
};

function PhoneBlock({ value, error, helperText, disabled }) {
  const { setFieldValue } = useFormikContext();
  return (
    <Box
      sx={{
        marginTop: '16px',
        '& .react-tel-input .form-control': { width: '100%', height: '56px' },
      }}
    >
      <PhoneNumberInput
        label="HCP Phone #"
        required
        helperText={helperText || 'Include country code'}
        value={value || ''}
        onChange={(v) => setFieldValue('hcp_phone', v)}
        error={!!error}
        defaultCountry="ae"
        fullWidth
        disabled={Boolean(disabled)}
      />
    </Box>
  );
}

HcpLicensesList.displayName = 'HcpLicensesList';

export default HcpLicensesList;
